/** Minimal toast system: a provider, a hook, and a live region. */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { IconButton } from '@/components/ui/Button'
import { cn } from '@/lib/cn'

export type ToastTone = 'info' | 'success' | 'warning' | 'error'

export interface Toast {
  id: number
  tone: ToastTone
  title: string
  description?: string
}

interface ToastContextValue {
  toasts: Toast[]
  push: (toast: Omit<Toast, 'id'>) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const AUTO_DISMISS_MS = 6000
let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const push = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = nextId++
      setToasts((current) => [...current, { ...toast, id }])
      // Errors stay until dismissed; everything else clears itself.
      if (toast.tone !== 'error') {
        setTimeout(() => dismiss(id), AUTO_DISMISS_MS)
      }
    },
    [dismiss],
  )

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport />
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside a ToastProvider')
  return context
}

const TONE_STYLES: Record<ToastTone, { rail: string; icon: string; glyph: ReactNode }> = {
  info: {
    rail: 'bg-accent',
    icon: 'text-accent',
    glyph: <path d="M8 7.5v4.5M8 4.5v.01" strokeWidth="1.75" strokeLinecap="round" />,
  },
  success: {
    rail: 'bg-good',
    icon: 'text-good',
    glyph: <path d="M4.5 8.5l2.5 2.5 4.5-5" strokeWidth="1.75" strokeLinecap="round" />,
  },
  warning: {
    rail: 'bg-warning',
    icon: 'text-warning',
    glyph: <path d="M8 5.5v3.5M8 11.5v.01" strokeWidth="1.75" strokeLinecap="round" />,
  },
  error: {
    rail: 'bg-critical',
    icon: 'text-critical',
    glyph: <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" strokeWidth="1.75" strokeLinecap="round" />,
  },
}

function ToastViewport() {
  const { toasts, dismiss } = useToast()

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-(--z-toast) flex flex-col items-center gap-2 p-4 sm:items-end"
    >
      {toasts.map((toast) => {
        const style = TONE_STYLES[toast.tone]
        return (
          <div
            key={toast.id}
            role={toast.tone === 'error' ? 'alert' : 'status'}
            className="animate-rise pointer-events-auto flex w-full max-w-sm overflow-hidden rounded-lg border border-line bg-surface-raised shadow-lg"
          >
            <span className={cn('w-1 shrink-0', style.rail)} aria-hidden="true" />
            <div className="flex min-w-0 flex-1 items-start gap-2.5 p-3">
              <svg
                viewBox="0 0 16 16"
                className={cn('mt-0.5 h-4 w-4 shrink-0', style.icon)}
                fill="none"
                stroke="currentColor"
                aria-hidden="true"
              >
                <circle cx="8" cy="8" r="6.5" strokeWidth="1.25" opacity="0.45" />
                {style.glyph}
              </svg>
              <div className="min-w-0 flex-1">
                <p className="text-base font-semibold text-ink">{toast.title}</p>
                {toast.description ? (
                  <p className="mt-0.5 text-sm break-words text-ink-secondary">
                    {toast.description}
                  </p>
                ) : null}
              </div>
              <IconButton
                label="Dismiss notification"
                variant="ghost"
                size="sm"
                onClick={() => dismiss(toast.id)}
                className="-my-1 -mr-1"
              >
                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden="true">
                  <path
                    d="M4 4l8 8M12 4l-8 8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </IconButton>
            </div>
          </div>
        )
      })}
    </div>
  )
}
